import { GridIcon } from '@radix-ui/react-icons';
import { AccessibleIcon, Avatar, Box, DropdownMenu, Flex, IconButton, Link, Text, ThemeToggle } from '@workos-inc/design-system';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useTheme } from 'next-themes';
import * as React from 'react';
import { ApiReferenceIcon } from 'src/icons/api-reference-icon';
import { useUser } from 'src/queries';
import { usePathname } from 'src/utils/use-pathname';
import { HeaderFeedback } from './header-feedback';
import { HeaderInlineSearch } from './header-inline-search';
import { NavigationMobileToggle } from './navigation-mobile';
import { NavigationPopover } from './navigation-popover';
import { Search } from './search';
interface HeaderProps {
  caption?: string;
}
export const Header = ({
  caption = 'Docs'
}: HeaderProps) => {
  const router = useRouter();
  const isHomePage = router.pathname === '/';
  const {
    theme,
    setTheme
  } = useTheme();
  const {
    isLoading,
    data: user
  } = useUser();

  // Whether to show Avatar skeleton **instead** of Sign In link when loading
  const [showAvatarSkeleton, setShowAvatarSkeleton] = React.useState(false);
  const pathname = usePathname();
  const [redirectUrl, setRedirectUrl] = React.useState('');

  // Derive hash from router.asPath so it updates on every Next.js navigation,
  // not just on `hashchange` events (which don't fire for client-side navigations).
  const hash = React.useMemo(() => {
    try {
      return new URL(router.asPath, 'https://workos.com').hash;
    } catch {
      return '';
    }
  }, [router.asPath]);
  React.useEffect(() => {
    const host = window.location.protocol + '//' + window.location.host;
    const newUrl = host + router.basePath + pathname + hash;
    setRedirectUrl(newUrl);
  }, [pathname, router, hash]);
  React.useEffect(() => {
    // Show the avatar skeleton if it looks like we are about to retrieve a user
    setShowAvatarSkeleton(isLoading && /_csrf=[^"]+;/.test(document.cookie));
  }, [isLoading]);
  return <div className="Header" data-sentry-component="Header" data-sentry-source-file="header.tsx">
      <div className="HeaderInner">
        <div className="HeaderLeft">
          <Box pl={{
          initial: '4',
          sm: '5',
          md: '6'
        }} data-sentry-element="Box" data-sentry-source-file="header.tsx">
            <Flex align="center" style={{
            marginLeft: -3
          }} data-sentry-element="Flex" data-sentry-source-file="header.tsx">
              <Link asChild className="HeaderLogoLink" data-sentry-element="Link" data-sentry-source-file="header.tsx">
                <NextLink href="/" data-sentry-element="NextLink" data-sentry-source-file="header.tsx">
                  <AccessibleIcon label="WorkOS Docs Homepage" data-sentry-element="AccessibleIcon" data-sentry-source-file="header.tsx">
                    <Logo data-sentry-element="Logo" data-sentry-source-file="header.tsx" />
                  </AccessibleIcon>
                </NextLink>
              </Link>

              {isHomePage ? <Box aria-hidden p="1">
                  <span className="HeaderLogoSubText">{caption}</span>
                </Box> : <>
                  <Flex display={{
                initial: 'none',
                md: 'inline-flex'
              }}>
                    <NavigationPopover>
                      <span className="HeaderLogoSubText" data-algolia-lvl0="">
                        {caption}
                      </span>
                      <LargeCaretDownIcon />
                    </NavigationPopover>
                  </Flex>

                  <Box display={{
                md: 'none'
              }} p="1">
                    <span className="HeaderLogoSubText">{caption}</span>
                  </Box>
                </>}
            </Flex>
          </Box>
        </div>

        <div className="HeaderControls">
          <div className="HeaderFullSearch">
            <Search data-sentry-element="Search" data-sentry-source-file="header.tsx" />
          </div>

          <div className="HeaderRight">
            <Text asChild size="2" wrap="nowrap" data-sentry-element="Text" data-sentry-source-file="header.tsx">
              <Flex data-header-inline-search-container align="center" gap="4" justify="end" pr={{
              initial: '4',
              sm: '5'
            }} data-sentry-element="Flex" data-sentry-source-file="header.tsx">
                <Flex align="center" display={{
                initial: 'none',
                md: 'flex'
              }} gap="4" data-sentry-element="Flex" data-sentry-source-file="header.tsx">
                  <HeaderInlineSearch data-sentry-element="HeaderInlineSearch" data-sentry-source-file="header.tsx" />
                  <Flex asChild align="center" display="inline-flex" gap="1" data-sentry-element="Flex" data-sentry-source-file="header.tsx">
                    <Link asChild color="gray" data-sentry-element="Link" data-sentry-source-file="header.tsx">
                      <NextLink href="/reference" data-sentry-element="NextLink" data-sentry-source-file="header.tsx">
                        <ApiReferenceIcon data-sentry-element="ApiReferenceIcon" data-sentry-source-file="header.tsx" />
                        API reference
                      </NextLink>
                    </Link>
                  </Flex>
                  <HeaderFeedback data-sentry-element="HeaderFeedback" data-sentry-source-file="header.tsx" />
                  <Flex asChild align="center" display="inline-flex" gap="1" data-sentry-element="Flex" data-sentry-source-file="header.tsx">
                    <Link color="gray" target="_blank" href={process.env.NEXT_PUBLIC_DASHBOARD_URL ?? 'https://dashboard.workos.com/'} data-sentry-element="Link" data-sentry-source-file="header.tsx">
                      <GridIcon data-sentry-element="GridIcon" data-sentry-source-file="header.tsx" />
                      Dashboard
                    </Link>
                  </Flex>

                  {!showAvatarSkeleton && !user && <Link color="gray" href={`${process.env.NEXT_PUBLIC_DASHBOARD_URL}/signin?redirect=${encodeURIComponent(redirectUrl)}`}>
                      Sign In
                    </Link>}
                </Flex>

                <Flex gap="4" data-sentry-element="Flex" data-sentry-source-file="header.tsx">
                  <ThemeToggle theme={theme} onThemeChange={newTheme => setTheme(newTheme)} data-sentry-element="ThemeToggle" data-sentry-source-file="header.tsx" />

                  {user && <Box display={{
                  initial: 'none',
                  md: 'block'
                }}>
                      <HeaderAvatar email={user.email} name={user.name} redirectUrl={redirectUrl} />
                    </Box>}

                  {showAvatarSkeleton && <Box style={{
                  width: 'var(--space-5)',
                  height: 'var(--space-5)',
                  background: 'var(--gray-a4)',
                  borderRadius: '100%'
                }} />}

                  <NavigationMobileToggle data-sentry-element="NavigationMobileToggle" data-sentry-source-file="header.tsx" />
                </Flex>
              </Flex>
            </Text>
          </div>
        </div>
      </div>
    </div>;
};
interface HeaderAvatarProps {
  name: string | null | undefined;
  email: string;
  redirectUrl: string;
}
const HeaderAvatar = ({
  name,
  email,
  redirectUrl
}: HeaderAvatarProps) => <DropdownMenu.Root data-sentry-element="DropdownMenu.Root" data-sentry-component="HeaderAvatar" data-sentry-source-file="header.tsx">
    <DropdownMenu.Trigger data-sentry-element="DropdownMenu.Trigger" data-sentry-source-file="header.tsx">
      <IconButton aria-label="Your profile" data-sentry-element="IconButton" data-sentry-source-file="header.tsx">
        <Avatar.Root src={`${process.env.NEXT_PUBLIC_DASHBOARD_URL}/api/avatar?name=${name}`} data-sentry-element="Avatar.Root" data-sentry-source-file="header.tsx">
          <Avatar.Text data-sentry-element="Avatar.Text" data-sentry-source-file="header.tsx">{(name ?? email).toUpperCase()[0]}</Avatar.Text>
        </Avatar.Root>
      </IconButton>
    </DropdownMenu.Trigger>

    <DropdownMenu.Content align="end" style={{
    minWidth: 148
  }} data-sentry-element="DropdownMenu.Content" data-sentry-source-file="header.tsx">
      <Flex align="start" direction="column" m="3" mb="1" data-sentry-element="Flex" data-sentry-source-file="header.tsx">
        <Text size="2" trim="start" weight="bold" data-sentry-element="Text" data-sentry-source-file="header.tsx">
          {name}
        </Text>
        <Text color="gray" size="2" data-sentry-element="Text" data-sentry-source-file="header.tsx">
          {email}
        </Text>
      </Flex>

      <DropdownMenu.Separator data-sentry-element="DropdownMenu.Separator" data-sentry-source-file="header.tsx" />

      <DropdownMenu.Item onSelect={() => {
      window.location.href = `${process.env.NEXT_PUBLIC_DASHBOARD_URL}/api/logout?redirect=${encodeURIComponent(redirectUrl)}`;
    }} data-sentry-element="DropdownMenu.Item" data-sentry-source-file="header.tsx">
        Sign out
      </DropdownMenu.Item>
    </DropdownMenu.Content>
  </DropdownMenu.Root>;
const Logo = (props: React.ComponentPropsWithoutRef<'svg'>) => <svg fill="var(--gray-12)" height="24" viewBox="0 0 108 20" width="108" xmlns="http://www.w3.org/2000/svg" {...props} data-sentry-element="svg" data-sentry-component="Logo" data-sentry-source-file="header.tsx">
    <path d="M34.8772 3.00105H32L35.7216 17.0027H39.0199L41.0916 8.52479C41.5125 6.68382 41.5514 5.85927 41.5514 5.85927H41.5903C41.5903 5.85927 41.6291 6.68382 42.0898 8.52479L44.2947 17.0035H47.4978L51.1417 3.00187H48.2644L46.2696 11.5368C45.8867 13.2058 45.8487 14.1073 45.8487 14.1073H45.7908C45.7908 14.1073 45.694 13.2058 45.2912 11.5368L43.2013 3.00187H39.9403L37.9654 11.5368C37.5816 13.1678 37.4477 14.1073 37.4477 14.1073H37.4088C37.4088 14.1073 37.3327 13.1479 36.949 11.5368L34.8772 3.00105Z M50.6612 11.9165C50.6612 8.71253 52.7329 6.64164 55.8599 6.64164C58.967 6.64164 61.0379 8.71253 61.0379 11.9156C61.0379 15.1377 58.9662 17.2293 55.8599 17.2293C52.7337 17.2285 50.6612 15.1386 50.6612 11.9165ZM58.3526 11.9165C58.3526 9.84474 57.3552 8.67449 55.8599 8.67449C54.2488 8.67449 53.3465 9.97872 53.3465 11.9165C53.3465 14.0262 54.3439 15.1956 55.8599 15.1956C57.4709 15.1956 58.3526 13.8914 58.3526 11.9165ZM62.3223 6.79795H64.8729V8.67862H64.9308C65.3724 7.70024 66.4261 6.72186 68.229 6.72186C68.535 6.72186 68.7277 6.76073 68.8617 6.79795V9.34852H68.7856C68.7856 9.34852 68.5557 9.27243 67.9222 9.27243C65.9472 9.27243 64.8729 10.4419 64.8729 12.6285V17.0011H62.3223V6.79795ZM70.0509 3.00104H72.6015V7.48935C72.6015 10.1359 72.5626 10.6155 72.5626 10.6155H72.6015L76.3992 6.79878H79.5825L75.1347 11.2102L80.2946 17.0027H77.2817L73.56 12.7824L72.6007 13.7219V17.0011H70.0501L70.0509 3.00104ZM80.331 10.0531C80.331 5.73769 83.0171 2.86127 87.0448 2.86127C91.0724 2.86127 93.7578 5.73769 93.7578 10.0531C93.7578 14.3686 91.0724 17.245 87.0448 17.245C83.0171 17.245 80.331 14.3686 80.331 10.0531ZM90.9757 10.0531C90.9757 7.11884 89.4035 5.14223 87.0431 5.14223C84.6828 5.14223 83.1123 7.11884 83.1123 10.0531C83.1123 12.9875 84.6836 14.9641 87.0431 14.9641C89.4018 14.9641 90.9757 12.9875 90.9757 10.0531ZM94.7337 12.3424H97.6498C97.6498 13.9923 98.782 14.9128 100.508 14.9128C101.965 14.9128 102.964 14.1833 102.964 13.1479C102.964 11.9785 102.197 11.6708 99.7596 11.192C97.5349 10.7503 95.1754 10.0019 95.1754 7.08658C95.1754 4.65097 97.2471 2.81 100.411 2.81C103.71 2.81 105.744 4.53601 105.744 7.14365H102.828C102.828 5.87829 101.85 5.09095 100.411 5.09095C98.9541 5.09095 98.0526 5.80055 98.0526 6.83599C98.0526 7.92933 98.6853 8.35029 100.642 8.75305C103.537 9.36754 105.916 9.69256 105.916 12.9147C105.916 15.4677 103.691 17.1937 100.411 17.1937C97.0742 17.1937 94.7337 15.2568 94.7337 12.3424Z" data-sentry-element="path" data-sentry-source-file="header.tsx" />
    <path d="M-3.22791e-06 10C-3.22791e-06 10.4383 0.114997 10.8767 0.33833 11.2542L4.385 18.2617C4.8 18.9767 5.43083 19.5617 6.215 19.8233C6.93574 20.0698 7.71976 20.0581 8.43278 19.79C9.14579 19.522 9.74343 19.0144 10.1233 18.3542L11.1 16.6617L7.24583 10L11.315 2.94583L12.2925 1.25417C12.5798 0.758583 12.9706 0.3308 13.4383 0L7.16166 0C6.06166 0 5.04583 0.585 4.5 1.53833L0.33833 8.74667C0.115967 9.12687 -0.000834046 9.55955 -3.22791e-06 10V10Z M23.0767 10C23.0767 9.56166 22.9617 9.1225 22.7383 8.74583L18.6383 1.64583C18.2559 0.988137 17.658 0.482704 16.9459 0.215002C16.2337 -0.0527 15.451 -0.0662429 14.73 0.176664C13.9467 0.43833 13.315 1.0225 12.9 1.73833L11.9767 3.33L15.8308 10L11.7617 17.0533L10.7842 18.7458C10.4925 19.2458 10.1 19.6692 9.63833 20H15.915C17.015 20 18.0308 19.415 18.5767 18.4617L22.7383 11.2533C22.9617 10.8767 23.0767 10.4383 23.0767 10V10Z" fill="var(--purple-9)" data-sentry-element="path" data-sentry-source-file="header.tsx" />
  </svg>;
const LargeCaretDownIcon = () => <svg fill="currentcolor" height="7" style={{
  marginTop: 4,
  marginRight: 2
}} viewBox="0 0 10 7" width="10" xmlns="http://www.w3.org/2000/svg" data-sentry-element="svg" data-sentry-component="LargeCaretDownIcon" data-sentry-source-file="header.tsx">
    <path d="M4.52271 5.71217L0.522705 1.71217L1.4773 0.757577L5 4.28028L8.52271 0.757577L9.4773 1.71217L5.4773 5.71217L5 6.18947L4.52271 5.71217Z" data-sentry-element="path" data-sentry-source-file="header.tsx" />
  </svg>;